import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ModalBodyComponent,
  ModalComponent,
  ModalFooterAction,
  ModalFooterComponent,
  ModalHeaderComponent,
  StateMessageComponent,
  TermValueComponent,
  type TermValueItem,
} from '@groundworks/ngx-groundworks-ui';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ForemanDetailInfo } from '../../sdk/sdk';
import { CalendarService } from '../../services/calendar.service';
import { ModalService } from '../../services/modal.service';

@Component({
  selector: 'app-foreman-detail-modal',
  templateUrl: './foreman-detail-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ModalComponent,
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    TermValueComponent,
    StateMessageComponent,
  ],
})
export class ForemanDetailModalComponent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly calendarService = inject(CalendarService);
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);

  readonly foremanUserId = signal('');
  readonly schedulingMode = signal(false);
  readonly detail = signal<ForemanDetailInfo | undefined>(undefined);
  readonly isLoading = signal(false);
  readonly hasError = signal(false);

  termValueItems = computed<TermValueItem[]>(() => {
    const d = this.detail();
    if (!d) return [];
    const projects = d.projectCapability?.canInstallProjectTypes ?? [];
    return [
      { term: 'Name', value: d.name ?? '' },
      { term: 'Status', value: String(d.status) },
      { term: 'Home Branch', value: d.homeBranch ?? '' },
      { term: 'IVPD', value: d.ivpd ?? 0, type: 'currency' as const },
      { term: 'Projects Can Install', value: projects.length > 0 ? projects.join(', ') : 'None' },
    ];
  });

  readonly footerActions = computed<ModalFooterAction[]>(() => {
    if (this.schedulingMode()) return [];
    return [
      {
        label: 'Configure Foreman',
        type: 'primary',
        action: () => this.onConfigureForeman(),
      },
    ];
  });

  retryButton = { text: 'Try Again', onClick: () => this.loadDetail(this.foremanUserId()) };

  constructor() {
    effect(() => {
      const userId = this.foremanUserId();
      if (userId) {
        this.loadDetail(userId);
      }
    });
  }

  private loadDetail(foremanUserId: string): void {
    this.isLoading.set(true);
    this.hasError.set(false);
    this.detail.set(undefined);
    this.calendarService
      .getForemanDetail(foremanUserId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (detail) => {
          this.detail.set(detail);
          this.isLoading.set(false);
        },
        error: (err: unknown) => {
          console.error('Failed to load foreman detail', { foremanUserId, err });
          this.isLoading.set(false);
          this.hasError.set(true);
        },
      });
  }

  closeModal(): void {
    this.activeModal.close();
  }

  onConfigureForeman(): void {
    this.activeModal.close();
    this.modalService.openForemanConfigModal(this.foremanUserId());
  }
}
